import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import {
  Briefcase, Building2, MapPin, Clock, ChevronRight, X, Sparkles, Send,
  CheckCircle, Search, Plus, Shield, Users, Edit, Trash2, Target,
  Star, ArrowRight, Zap, Eye, Layers, Filter, MessageSquare, Crown,
  Activity
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const user = () => {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
};

const getJobTypeLabel = (t, type) => {
  const keys = {
    'full-time': 'type_full_time', 'part-time': 'type_part_time',
    'contract': 'type_contract', 'internship': 'type_internship', 'remote': 'type_remote'
  };
  return t(keys[type] || type);
};

const getAppStatusMeta = (t) => ({
  pending: { bg: 'rgba(99,102,241,0.12)', color: '#818cf8', label: t('status_applied') },
  shortlisted: { bg: 'rgba(34,211,238,0.12)', color: '#22d3ee', label: t('status_shortlisted') },
  hired: { bg: 'rgba(34,197,94,0.12)', color: '#4ade80', label: t('status_hired') },
  rejected: { bg: 'rgba(244,114,182,0.1)', color: '#f472b6', label: t('status_rejected') },
});

function ScoreBadge({ score }) {
  if (score == null) return null;
  const c = score >= 70 ? '#4ade80' : score >= 40 ? '#facc15' : '#f472b6';
  const bg = score >= 70 ? 'rgba(34,197,94,0.1)' : score >= 40 ? 'rgba(250,204,21,0.1)' : 'rgba(244,114,182,0.1)';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
      padding: '0.35rem 0.8rem', borderRadius: '10px',
      background: bg, color: c, fontSize: '0.78rem', fontWeight: 800,
      border: `1px solid ${c}20`,
      boxShadow: `0 4px 12px ${c}15`
    }}>
      <Sparkles size={12} strokeWidth={2.5} />
      <span>{score}% <span style={{ opacity: 0.6, fontSize: '0.65rem' }}>MATCH</span></span>
    </div>
  );
}

// ─── Apply Modal ──────────────────────────────────────────────────────────────
function ApplyModal({ job, onClose, onSuccess }) {
  const { t } = useLanguage();
  const [coverLetter, setCoverLetter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post(`/jobs/${job.id}/apply`, { cover_letter: coverLetter });
      onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.error || t('error_generic'));
    }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '520px', padding: '2rem', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.4rem' }}><X size={18} /></button>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-heading)', marginBottom: '0.25rem' }}>{job.title}</h3>
        <div style={{ color: 'var(--accent-primary)', fontWeight: 600, fontSize: '0.9rem', marginBottom: '1.5rem' }}>{job.company_name}</div>
        {error && <div style={{ background: 'rgba(244,114,182,0.1)', border: '1px solid rgba(244,114,182,0.2)', color: '#f472b6', padding: '0.75rem', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.85rem', fontWeight: 600 }}>{error}</div>}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <textarea className="cyber-input" rows={4} placeholder={t('cover_letter_placeholder')} value={coverLetter} onChange={e => setCoverLetter(e.target.value)} disabled={loading} style={{ resize: 'vertical', minHeight: '100px', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', background: 'rgba(99,102,241,0.06)', borderRadius: '10px', padding: '0.75rem', border: '1px solid rgba(99,102,241,0.15)', fontSize: '0.82rem', color: 'var(--text-body)' }}>
            <Sparkles size={16} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
            <span>{t('ai_matching')}</span>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="button" onClick={onClose} disabled={loading} className="btn-cyber" style={{ flex: 1, padding: '0.75rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', color: 'var(--text-body)' }}>{t('cancel')}</button>
            <button type="submit" disabled={loading} className="btn-cyber" style={{ flex: 2, padding: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', opacity: loading ? 0.7 : 1 }}><Send size={16} /> {loading ? t('loading') : t('submit')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Opportunity Form Modal ───────────────────────────────────────────────────
function OppFormModal({ opportunity, onClose, onSave }) {
  const { t } = useLanguage();
  const isEdit = !!opportunity?.id;
  const [form, setForm] = useState({
    title: opportunity?.title || '',
    description: opportunity?.description || '',
    category: opportunity?.category || 'Project',
    budget: opportunity?.budget || '',
    timeline: opportunity?.timeline || '',
    location: opportunity?.location || t('remote'),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isEdit) {
        await api.put(`/opportunities/${opportunity.id}`, form);
      } else {
        await api.post('/opportunities', form);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || t('error_generic'));
    }
    setLoading(false);
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '0.75rem 1rem', background: 'var(--bg-deep)', border: '1px solid var(--glass-border)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.9rem', outline: 'none' };
  const labelStyle = { fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem', display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '520px', padding: '2rem', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '8px', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.4rem' }}><X size={18} /></button>
        <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--text-heading)', marginBottom: '1.5rem' }}>{isEdit ? t('edit_opportunity') : t('new_opportunity')}</h3>
        {error && <div style={{ background: 'rgba(244,114,182,0.1)', border: '1px solid rgba(244,114,182,0.2)', color: '#f472b6', padding: '0.75rem', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.85rem', fontWeight: 600 }}>{error}</div>}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div><label style={labelStyle}>{t('title_label')} *</label><input style={inputStyle} required value={form.title} onChange={set('title')} /></div>
          <div>
            <label style={labelStyle}>{t('category_label')}</label>
            <select style={inputStyle} value={form.category} onChange={set('category')}>
              <option value="Project">{t('category_project')}</option>
              <option value="Job">{t('category_job')}</option>
              <option value="Collaboration">{t('category_collab')}</option>
            </select>
          </div>
          <div className="grid-to-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div><label style={labelStyle}>{t('budget_label')}</label><input style={inputStyle} value={form.budget} onChange={set('budget')} placeholder={t('budget_placeholder')} /></div>
            <div><label style={labelStyle}>{t('timeline_label')}</label><input style={inputStyle} value={form.timeline} onChange={set('timeline')} placeholder={t('timeline_placeholder')} /></div>
          </div>
          <div><label style={labelStyle}>{t('location_label')}</label><input style={inputStyle} value={form.location} onChange={set('location')} placeholder={t('location_placeholder')} /></div>
          <div><label style={labelStyle}>{t('description_label')} *</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' }} required value={form.description} onChange={set('description')} /></div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="button" onClick={onClose} disabled={loading} className="btn-cyber" style={{ flex: 1, padding: '0.75rem', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', color: 'var(--text-body)' }}>{t('cancel')}</button>
            <button type="submit" disabled={loading} className="btn-cyber" style={{ flex: 2, padding: '0.75rem', opacity: loading ? 0.7 : 1 }}>{loading ? t('saving') : (isEdit ? t('save_mission') : t('create_mission'))}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Opportunity Card ─────────────────────────────────────────────────────────
function OppCard({ opp, currentUser, onEdit, onDelete, onInitiate, onTrack }) {
  const { t } = useLanguage();
  const isOwner = opp.user_id === currentUser?.id;
  const isAdmin = currentUser?.role === 'ap_admin';
  const canAct = currentUser?.role !== 'volunteer' && currentUser?.role !== 'guest';

  const catColors = {
    Project: { bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.15)', color: 'var(--accent-primary)', icon: Layers },
    Job: { bg: 'rgba(244,114,182,0.08)', border: 'rgba(244,114,182,0.15)', color: 'var(--accent-magenta)', icon: Building2 },
    Collaboration: { bg: 'rgba(34,211,238,0.08)', border: 'rgba(34,211,238,0.15)', color: 'var(--accent-cyan)', icon: Users },
  };
  const cc = catColors[opp.category] || catColors.Project;
  const CatIcon = cc.icon;

  const initials = (opp.full_name || opp.username || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="glass-card board-hover-card" style={{ padding: '1.5rem', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', border: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.02)', borderRadius: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div style={{
            width: '46px', height: '46px', borderRadius: '12px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--glass-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1rem', fontWeight: 900, color: cc.color,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
          }}>
            {initials}
          </div>
          <div>
            <h4 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-heading)', marginBottom: '0.15rem', letterSpacing: '-0.01em' }}>{opp.title}</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>
              <span style={{ color: 'var(--accent-cyan)' }}>by</span> {opp.full_name || opp.username}
              <span style={{ opacity: 0.2 }}>•</span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                padding: '0.2rem 0.5rem', borderRadius: '6px',
                background: cc.bg, color: cc.color, fontSize: '0.65rem', fontWeight: 800,
                textTransform: 'uppercase', letterSpacing: '0.05em', border: `1px solid ${cc.border}`
              }}>
                <CatIcon size={11} strokeWidth={2.5} /> {opp.category}
              </span>
            </div>
          </div>
        </div>

        {(isOwner || isAdmin) && (
          <div style={{ display: 'flex', gap: '0.4rem', padding: '0.35rem', borderRadius: '10px', background: 'rgba(0,0,0,0.15)', border: '1px solid var(--glass-border)' }}>
            <button onClick={() => onEdit(opp)} className="hover-bright" style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.4rem', display: 'flex' }}><Edit size={14} /></button>
            <button onClick={() => onDelete(opp)} className="hover-bright" style={{ background: 'transparent', border: 'none', color: '#f472b6', cursor: 'pointer', padding: '0.4rem', display: 'flex' }}><Trash2 size={14} /></button>
          </div>
        )}
      </div>

      <p style={{ color: 'var(--text-body)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.25rem', opacity: 0.85, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{opp.description}</p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: '0.75rem', marginBottom: '1.5rem', padding: '1rem',
        background: 'rgba(0,0,0,0.15)', borderRadius: '12px', border: '1px solid var(--glass-border)'
      }}>
        {[
          { label: 'Location', val: opp.location || 'Remote', icon: MapPin, col: 'var(--accent-primary)' },
          { label: 'Budget', val: opp.budget || 'Strategic', icon: Zap, col: '#4ade80' },
          { label: 'Timeline', val: opp.timeline || 'Flexible', icon: Clock, col: 'var(--accent-cyan)' },
          { label: 'Status', val: opp.status, icon: Activity, col: opp.status === 'open' ? '#4ade80' : 'var(--text-muted)' }
        ].map(m => (
          <div key={m.label} style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</span>
            <span style={{ fontSize: '0.8rem', color: m.label === 'Status' ? m.col : 'var(--text-heading)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <m.icon size={11} color={m.col} fill={m.label === 'Budget' ? m.col : 'none'} /> {m.val}
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        {canAct && opp.status === 'open' && !isOwner && (
          <button
            onClick={() => onInitiate(opp)}
            className="mission-btn"
            style={{
              padding: '0.65rem 1.5rem', fontSize: '0.8rem',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              background: 'white', color: 'black', fontWeight: 900,
              borderRadius: '10px', border: 'none', cursor: 'pointer'
            }}
          >
            <Zap size={15} fill="black" /> {t('initiate').toUpperCase()}
          </button>
        )}
        {canAct && (
          <button
            onClick={() => onTrack(opp)}
            className="hover-bright"
            style={{
              padding: '0.65rem 1.25rem', fontSize: '0.8rem',
              background: opp.is_tracking ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${opp.is_tracking ? 'var(--accent-primary)' : 'var(--glass-border)'}`,
              color: opp.is_tracking ? 'var(--accent-primary)' : 'var(--text-body)',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              borderRadius: '10px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s'
            }}
          >
            <Eye size={15} fill={opp.is_tracking ? 'var(--accent-primary)' : 'none'} />
            {opp.is_tracking ? t('tracking') : t('track')}
            <span style={{ opacity: 0.5, fontSize: '0.72rem', marginLeft: '0.1rem' }}>{opp.tracker_count || 0}</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Job Card ─────────────────────────────────────────────────────────────────
function JobCard({ job, isSelected, onClick }) {
  const { t } = useLanguage();
  const APP_STATUS = getAppStatusMeta(t);
  const initials = (job.company_name || 'C').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div
      onClick={() => onClick(job)}
      className="glass-card board-hover-card"
      style={{
        padding: '1.25rem', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: `1px solid ${isSelected ? 'var(--accent-primary)' : 'var(--glass-border)'}`,
        background: isSelected ? 'rgba(99,102,241,0.05)' : 'rgba(255,255,255,0.02)',
        transform: isSelected ? 'translateX(6px)' : 'none',
        borderRadius: '16px', position: 'relative', overflow: 'hidden'
      }}
    >

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <div style={{
          width: '46px', height: '46px', borderRadius: '12px', flexShrink: 0,
          background: job.is_ap_exclusive ? 'linear-gradient(135deg, var(--accent-magenta), #ec4899)' : 'rgba(255,255,255,0.03)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1rem', fontWeight: 900,
          color: job.is_ap_exclusive ? 'white' : 'var(--accent-primary)',
          border: `1px solid ${job.is_ap_exclusive ? 'rgba(244,114,182,0.3)' : 'var(--glass-border)'}`,
          boxShadow: job.is_ap_exclusive ? '0 8px 24px rgba(244,114,182,0.2)' : 'none'
        }}>
          {job.is_ap_exclusive ? <Crown size={20} color="white" /> : initials}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.2rem' }}>
            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-heading)', marginBottom: '0.1rem', letterSpacing: '-0.01em' }}>{job.title}</h4>
              <div style={{ fontSize: '0.8rem', color: 'var(--accent-primary)', fontWeight: 700, letterSpacing: '0.02em' }}>{job.company_name}</div>
            </div>
            {job.user_applied && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
                <span style={{
                  fontSize: '0.6rem', padding: '0.2rem 0.5rem', borderRadius: '6px',
                  fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em',
                  background: APP_STATUS[job.user_application_status]?.bg,
                  color: APP_STATUS[job.user_application_status]?.color,
                  border: `1px solid ${APP_STATUS[job.user_application_status]?.color}20`
                }}>
                  {APP_STATUS[job.user_application_status]?.label}
                </span>
                <ScoreBadge score={job.user_ai_score} />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
              <MapPin size={11} color="var(--accent-primary)" /> {job.location || 'Remote'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
              <Clock size={11} color="var(--accent-cyan)" /> {getJobTypeLabel(t, job.type)}
            </div>
            {job.salary_range && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: '#4ade80', fontWeight: 700 }}>
                <Target size={11} /> {job.salary_range}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Job Detail Panel ─────────────────────────────────────────────────────────
function JobDetail({ job, currentUser, onApply, onClose }) {
  const { t } = useLanguage();
  if (!job) return null;
  const APP_STATUS = getAppStatusMeta(t);
  const deadline = job.deadline ? new Date(job.deadline) : null;
  const isExpired = deadline && deadline < new Date();
  const canApply = ['member', 'ap_admin', 'volunteer'].includes(currentUser?.role);
  const initials = (job.company_name || 'C').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="glass-card animate-fade-in" style={{ padding: '2rem', position: 'sticky', top: '1.5rem', border: '1px solid var(--accent-primary)20', background: 'rgba(255,255,255,0.015)', borderRadius: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '16px',
          background: job.is_ap_exclusive ? 'linear-gradient(135deg, var(--accent-magenta), #ec4899)' : 'rgba(255,255,255,0.03)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.25rem', fontWeight: 900, color: job.is_ap_exclusive ? 'white' : 'var(--accent-primary)',
          boxShadow: job.is_ap_exclusive ? '0 10px 30px rgba(244,114,182,0.25)' : 'none',
          border: '1px solid var(--glass-border)'
        }}>
          {job.is_ap_exclusive ? <Crown size={26} /> : initials}
        </div>
        <button
          onClick={onClose}
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.5rem', borderRadius: '10px', display: 'flex' }}
          className="hover-bright"
        >
          <X size={18} />
        </button>
      </div>

      <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
          <h3 style={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.03em', lineHeight: 1.1 }}>{job.title}</h3>
          {job.is_ap_exclusive && (
            <div style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', background: 'rgba(244,114,182,0.1)', color: '#f472b6', fontSize: '0.6rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', border: '1px solid rgba(244,114,182,0.15)' }}>
              {t('ap_exclusive')}
            </div>
          )}
        </div>
        <div style={{ color: 'var(--accent-primary)', fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.01em' }}>{job.company_name}</div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.85rem', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid var(--glass-border)', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
          <MapPin size={13} color="var(--accent-primary)" /> {job.location || 'Remote'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.85rem', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid var(--glass-border)', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
          <Clock size={13} color="var(--accent-cyan)" /> {getJobTypeLabel(t, job.type)}
        </div>
        {job.salary_range && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.85rem', background: 'rgba(34,197,94,0.05)', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.15)', fontSize: '0.8rem', color: '#4ade80', fontWeight: 700 }}>
            <Target size={13} /> {job.salary_range}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
          <div style={{ width: '16px', height: '1px', background: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '0.65rem', fontWeight: 900, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('job_description')}</span>
        </div>
        <p style={{ color: 'var(--text-body)', lineHeight: 1.7, fontSize: '0.92rem', whiteSpace: 'pre-wrap', opacity: 0.85 }}>{job.description}</p>
      </div>

      {job.required_skills?.length > 0 && (
        <div style={{ marginBottom: '2.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
            <div style={{ width: '16px', height: '1px', background: 'var(--accent-cyan)' }} />
            <span style={{ fontSize: '0.65rem', fontWeight: 900, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Strategic Skills</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {job.required_skills.map((s, i) => (
              <span key={i} style={{ padding: '0.35rem 0.85rem', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700, background: 'rgba(99,102,241,0.05)', color: 'white', border: '1px solid rgba(99,102,241,0.15)' }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 'auto', paddingTop: '1.5rem', borderTop: '1px solid var(--glass-border)' }}>
        {deadline && (
          <div style={{ marginBottom: '1.25rem', padding: '1rem', borderRadius: '12px', background: isExpired ? 'rgba(244,114,182,0.03)' : 'rgba(255,255,255,0.02)', border: `1px solid ${isExpired ? 'rgba(244,114,182,0.15)' : 'var(--glass-border)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700 }}>{t('deadline')}</span>
            <span style={{ fontSize: '0.8rem', color: isExpired ? '#f472b6' : 'var(--text-heading)', fontWeight: 800 }}>
              {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(deadline)}
            </span>
          </div>
        )}

        {canApply && !job.user_applied && (
          <button
            disabled={isExpired}
            onClick={() => onApply(job)}
            className="mission-btn"
            style={{
              width: '100%', padding: '1rem', borderRadius: '14px',
              background: 'linear-gradient(135deg, var(--accent-primary), #6366f1)',
              color: 'white', border: 'none', cursor: isExpired ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem', fontWeight: 900, letterSpacing: '0.05em',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
              boxShadow: '0 10px 25px rgba(99,102,241,0.3)', opacity: isExpired ? 0.5 : 1
            }}
          >
            <Send size={18} color="white" /> {t('apply_now').toUpperCase()}
          </button>
        )}
        {job.user_applied && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', padding: '1rem', borderRadius: '14px', fontWeight: 900, background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.15)', color: '#4ade80', fontSize: '0.9rem', textTransform: 'uppercase' }}>
              <CheckCircle size={20} fill="rgba(34,197,94,0.1)" /> {t('already_applied')}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
               <ScoreBadge score={job.user_ai_score} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN BOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
function Board() {
  const { t } = useLanguage();
  const currentUser = user();
  const role = currentUser?.role;

  const tabs = [
    { id: 'opportunities', label: t('opportunities_tab'), icon: Briefcase },
    { id: 'jobs', label: t('jobs_tab'), icon: Building2 },
  ];
  if (['member', 'ap_admin'].includes(role)) {
    tabs.push({ id: 'internships', label: t('internships_tab'), icon: Crown });
  }

  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'opportunities');
  const [search, setSearch] = useState('');

  // ─── Opportunities state ────────────────────────────────────────────────
  const [opps, setOpps] = useState([]);
  const [oppsLoading, setOppsLoading] = useState(false);
  const [oppModal, setOppModal] = useState(null);

  // ─── Jobs state ─────────────────────────────────────────────────────────
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyingJob, setApplyingJob] = useState(null);
  const [applySuccess, setApplySuccess] = useState(null);

  // ─── Fetch functions ────────────────────────────────────────────────────
  const fetchOpps = useCallback(async () => {
    setOppsLoading(true);
    try {
      const { data } = await api.get('/opportunities');
      setOpps(Array.isArray(data) ? data : []);
    } catch { setOpps([]); }
    setOppsLoading(false);
  }, []);

  const fetchJobs = useCallback(async (apExclusive = false) => {
    setJobsLoading(true);
    try {
      const { data } = await api.get(`/jobs?is_ap_exclusive=${apExclusive}`);
      setJobs(Array.isArray(data) ? data : []);
    } catch { setJobs([]); }
    setJobsLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'opportunities') fetchOpps();
    else if (activeTab === 'jobs') fetchJobs(false);
    else if (activeTab === 'internships') fetchJobs(true);
    setSelectedJob(null);
    setSearch('');
  }, [activeTab]);

  // ─── Opp handlers ──────────────────────────────────────────────────────
  const handleOppSave = () => { setOppModal(null); fetchOpps(); };
  const handleOppDelete = async (opp) => {
    if (!window.confirm(t('delete_opp_confirm'))) return;
    try { await api.delete(`/opportunities/${opp.id}`); fetchOpps(); } catch { }
  };
  const handleOppInitiate = async (opp) => {
    try { await api.put(`/opportunities/${opp.id}/initiate`); fetchOpps(); } catch (err) { alert(err.response?.data?.error || t('error_generic')); }
  };
  const handleOppTrack = async (opp) => {
    try { await api.post(`/opportunities/${opp.id}/track`); fetchOpps(); } catch { }
  };

  // ─── Job handlers ──────────────────────────────────────────────────────
  const handleApplySuccess = (data) => {
    setApplyingJob(null);
    setApplySuccess(data);
    fetchJobs(activeTab === 'internships');
    setTimeout(() => setApplySuccess(null), 4000);
  };

  // ─── Filtering ─────────────────────────────────────────────────────────
  const q = search.toLowerCase();
  const filteredOpps = opps.filter(o => !q || o.title?.toLowerCase().includes(q) || o.description?.toLowerCase().includes(q) || o.category?.toLowerCase().includes(q));
  const filteredJobs = jobs.filter(j => !q || j.title?.toLowerCase().includes(q) || j.company_name?.toLowerCase().includes(q) || (j.required_skills || []).some(s => s.toLowerCase().includes(q)));

  const canPost = ['member', 'ap_admin'].includes(role);

  return (
    <div className="container" style={{ paddingBottom: '3rem' }}>
      <style>{`
        .board-hover-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05) !important;
          border-color: var(--accent-primary)40 !important;
        }
        .admin-tab-btn {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 1.1rem 1.75rem;
          background: transparent;
          border: none;
          color: var(--text-muted);
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
        }
        .admin-tab-btn::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 1.75rem;
          right: 1.75rem;
          height: 2px;
          background: var(--accent-primary);
          opacity: 0;
          transform: scaleX(0);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 2px;
        }
        .admin-tab-btn.active {
          color: var(--accent-primary);
          font-weight: 800;
        }
        .admin-tab-btn.active::after {
          opacity: 1;
          transform: scaleX(1);
        }
        .admin-tab-btn:hover:not(.active) {
          color: var(--text-heading);
        }
      `}</style>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '3.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '0.6rem'
        }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '12px',
            background: 'linear-gradient(135deg, var(--accent-primary), rgba(139,92,246,0.8))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px -6px rgba(99,102,241,0.5)',
            border: '1px solid rgba(255,255,255,0.2)',
            transform: 'rotate(-4deg)'
          }}>
            <Layers size={22} color="white" />
          </div>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.04em', lineHeight: 1.1 }}>
            {t('jobs_title').split(' ')[0]} <span style={{
              background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>{t('jobs_title').split(' ').slice(1).join(' ')}</span>
          </h2>
        </div>
        <p style={{ color: 'var(--text-muted)', fontWeight: 500, fontSize: '1rem', letterSpacing: '0.01em', opacity: 0.8 }}>{t('board_subtitle')}</p>
      </div>

      {/* ── Tab Navigation (Admin Style) ─────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'center', gap: '1rem',
        marginBottom: '2.5rem', borderBottom: '1px solid var(--glass-border)'
      }}>
        {tabs.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`admin-tab-btn ${active ? 'active' : ''}`}
            >
              <tab.icon size={17} style={{ opacity: active ? 1 : 0.6 }} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Command Control Bar ─────────────────────────────────────────── */}
      <div className="glass-card" style={{
        padding: '1.25rem 2rem', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '2.5rem',
        border: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.015)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: '320px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={16} style={{
              position: 'absolute', left: '1rem', top: '50%',
              transform: 'translateY(-50%)', color: 'var(--text-muted)'
            }} />
            <input
              style={{
                width: '100%', padding: '0.85rem 1rem 0.85rem 3rem', borderRadius: '12px',
                background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)',
                color: 'white', fontWeight: 600, fontSize: '0.9rem'
              }}
              placeholder={t('search_jobs')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.4rem 1rem', background: 'rgba(255,255,255,0.03)',
            borderRadius: '999px', border: '1px solid rgba(255,255,255,0.04)'
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {(activeTab === 'opportunities' ? oppsLoading : jobsLoading)
                ? t('loading')
                : `${activeTab === 'opportunities' ? filteredOpps.length : filteredJobs.length} RESULT${(activeTab === 'opportunities' ? filteredOpps.length : filteredJobs.length) === 1 ? '' : 'S'}`
              }
            </span>
          </div>
        </div>

        {activeTab === 'opportunities' && canPost && (
          <button
            onClick={() => setOppModal({})}
            className="mission-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: '0 1.75rem', height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, var(--accent-primary), #6366f1)',
              fontWeight: 900, letterSpacing: '0.04em', color: 'white',
              border: 'none', cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(99,102,241,0.3)',
              fontSize: '0.85rem'
            }}
          >
            <Plus size={18} strokeWidth={3} /> {t('new_opportunity').toUpperCase()}
          </button>
        )}
      </div>


      {/* ── Opportunities Tab ─────────────────────────────────────────────── */}
      {activeTab === 'opportunities' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {oppsLoading ? (
            Array.from({ length: 3 }).map((_, i) => <div key={i} className="glass-card" style={{ padding: '1.5rem', opacity: 0.3, height: '80px' }} />)
          ) : filteredOpps.length === 0 ? (
            <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Briefcase size={36} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <div style={{ fontWeight: 600 }}>{t('no_jobs')}</div>
            </div>
          ) : (
            filteredOpps.map(opp => (
              <OppCard
                key={opp.id}
                opp={opp}
                currentUser={currentUser}
                onEdit={o => setOppModal(o)}
                onDelete={handleOppDelete}
                onInitiate={handleOppInitiate}
                onTrack={handleOppTrack}
              />
            ))
          )}
        </div>
      )}

      {/* ── Jobs / Internships Tab ────────────────────────────────────────── */}
      {(activeTab === 'jobs' || activeTab === 'internships') && (
        <div className="profile-grid grid-to-stack" style={{ display: 'grid', gridTemplateColumns: selectedJob ? '1fr 1fr' : '1fr', gap: '1.25rem', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {jobsLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="glass-card" style={{ padding: '2rem', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--glass-border)', borderRadius: '16px', animation: 'pulse 2s infinite' }}>
                  <div style={{ display: 'flex', gap: '1.25rem' }}>
                    <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'rgba(255,255,255,0.05)' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ width: '60%', height: '14px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', marginBottom: '0.75rem' }} />
                      <div style={{ width: '30%', height: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '4px' }} />
                    </div>
                  </div>
                </div>
              ))
            ) : filteredJobs.length === 0 ? (
              <div className="glass-card" style={{ padding: '6rem 2rem', textAlign: 'center', background: 'rgba(255,255,255,0.01)', border: '1px dashed var(--glass-border)', borderRadius: '24px' }}>
                <div style={{ position: 'relative', width: 'fit-content', margin: '0 auto 2rem' }}>
                  <Building2 size={64} style={{ opacity: 0.05 }} />
                  <Target size={32} color="var(--accent-primary)" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', opacity: 0.5 }} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-heading)', marginBottom: '0.5rem' }}>{activeTab === 'internships' ? t('no_internships') : t('no_jobs_found')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', maxWidth: '300px', margin: '0 auto' }}>{t('search_jobs_desc_empty') || 'Try adjusting your search criteria or explore other categories.'}</p>
              </div>
            ) : (
              filteredJobs.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  isSelected={selectedJob?.id === job.id}
                  onClick={j => setSelectedJob(prev => prev?.id === j.id ? null : j)}
                />
              ))
            )}
          </div>
          {selectedJob && (
            <JobDetail job={selectedJob} currentUser={currentUser} onApply={j => setApplyingJob(j)} onClose={() => setSelectedJob(null)} />
          )}
        </div>
      )}

      {/* Modals */}
      {oppModal !== null && <OppFormModal opportunity={oppModal?.id ? oppModal : null} onClose={() => setOppModal(null)} onSave={handleOppSave} />}
      {applyingJob && <ApplyModal job={applyingJob} onClose={() => setApplyingJob(null)} onSuccess={handleApplySuccess} />}
    </div>
  );
}

export default Board;
